<template>
  <div class="staff-settings">
    <b-card title="Staff Settings">
      <b-overlay
        :show="loading"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-form
          @submit.prevent
        >
          <b-row>
            <b-card
              title="Staff Categorization"
              class="mb-0 mt-0 w-100"
            >
              <b-form-radio-group
                v-model="staffCategorizationEnabled"
                :options="radioOptions"
                class="demo-inline-spacing"
                value-field="value"
                text-field="text"
                disabled-field="disabled"
                @change="updateStaffSettings"
              />
            </b-card>
          </b-row>
          <b-row>
            <div class="ml-2">
              Enable custom staff groups and category-specific employee ID generation. When off, the system uses the default ST-00000 format for every new staff member.
            </div>
          </b-row>
        </b-form>
      </b-overlay>
    </b-card>

    <b-card
      v-if="staffCategorizationEnabled"
      no-body
      class="mt-2"
    >
      <div>
        <b-row>
          <b-col
            cols="12"
            md="8"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0 mt-1 ml-1"
          >
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              class="custom-button-color add-new-button"
              @click="onClickOpenAddNewModal"
            >
              <feather-icon icon="PlusIcon" />
              Add New
            </b-button>
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-end mb-1 mb-md-0 mt-1 mr-1"
          >
            <ReportTableButtons
              :items="rows"
              :json_fieldz="json_fields"
              :name="`StaffCategoryTypes - Page ${page}.xls`"
              :bulkname="`StaffCategoryTypes.xls`"
              :fetch="getStaffCategoryTypesListNoPagination"
              @refresh="onClickRefresh"
            />
          </b-col>
        </b-row>
      </div>
      <b-overlay
        id="overlay-background"
        class="mt-2"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-table
          hover
          responsive
          striped
          show-empty
          :per-page="perPage"
          :items="rows"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          sticky-header="100vh"
          class="hide-vertical-scroll"
        >
          <template #empty>
            <TableDataFetching
              :rows="rows"
              :data-loading="dataLoading"
            />
          </template>
          <template #cell(number_of_digits)="data">
            <div class="content-body-cell">
              {{ formatDigitLength(data.value) }}
            </div>
          </template>
          <template #cell(is_active)="data">
            <b-badge :variant="data.value ? 'light-success' : 'light-danger'">
              {{ data.value ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>
          <template #cell(action)="data">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              :class="rows.length !== 1 ? 'dropdown-no-scroll' : ''"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="12"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item
                @click="onClickStatusUpdate(data.item.id, !data.item.is_active)"
              >
                <feather-icon icon="ToolIcon" />
                {{ data.item.is_active ? 'Inactive' : 'Active' }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-overlay>
      <div class="mx-2 mb-2">
        <b-row cols="12">
          <b-col
            class="d-flex align-items-center justify-content-sm-start"
            md="3"
          >
            <label>Per page</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-1 width-100 mr-1"
            />
          </b-col>
          <b-col
            md="9"
            class="d-flex align-items-center justify-content-end"
          >
            <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="(meta.total) ? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <add-staff-category-modal ref="add_staff_category_modal" />
  </div>
</template>

<script>
import {
  BRow,
  BForm,
  BCard,
  BOverlay,
  BFormRadioGroup,
  BTable,
  BButton,
  BCol,
  BDropdown,
  BDropdownItem,
  BPagination,
  BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import AddStaffCategoryModal from '@/components/staffCategory/AddStaffCategoryModal.vue'

const StaffSettingsRepository = RepositoryFactory.get('staffSetting')

export default {
  components: {
    BRow,
    BForm,
    BCard,
    BOverlay,
    BFormRadioGroup,
    BTable,
    BButton,
    BCol,
    BDropdown,
    BDropdownItem,
    BPagination,
    BBadge,
    AddStaffCategoryModal,
    'v-select': vSelect,
  },
  directives: {
    Ripple,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      dataLoading: false,
      staffCategorizationEnabled: false,
      radioOptions: [
        { text: 'On', value: true },
        { text: 'Off', value: false },
      ],
      meta: {},
      perPage: 20,
      pageOptions: [10, 20, 50],
      page: 1,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      fields: [
        {
          key: 'name', label: 'Staff Type Name', sortable: true, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'prefix', label: 'ID Prefix', sortable: true, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'start_from', label: 'Starting Number', sortable: true, thClass: ' text-right', tdClass: ' text-right',
        },
        {
          key: 'number_of_digits', label: 'Digit Length', sortable: true, thClass: ' text-right', tdClass: ' text-right',
        },
        {
          key: 'preview', label: 'Preview', sortable: true, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'is_active', label: 'Is Active', sortable: true, thClass: ' text-center', tdClass: ' text-center',
        },
        {
          key: 'action', label: 'Action', sortable: false, thClass: ' text-center', tdClass: ' text-center',
        },
      ],
      json_fields: {
        'Staff Type Name': 'name',
        'ID Prefix': 'prefix',
        'Starting Number': 'start_from',
        'Digit Length': {
          field: 'number_of_digits',
          callback: value => `"${String(value).padStart(2, '0')}"`,
        },
        Preview: 'preview',
        'Is Active': {
          field: 'is_active',
          callback: value => `"${value ? 'Active' : 'Inactive'}"`,
        },
      },
      rows: [],
    }
  },
  watch: {
    page() {
      this.getStaffCategoryTypesList()
    },
    perPage() {
      this.getStaffCategoryTypesList()
    },
    staffCategorizationEnabled(value) {
      if (value) {
        this.getStaffCategoryTypesList()
      }
    },
  },
  mounted() {
    this.readStaffSettings()
  },
  methods: {
    formatDigitLength(value) {
      if (value === null || value === undefined || value === '') return ''
      return String(value).padStart(2, '0')
    },
    onClickOpenAddNewModal() {
      this.$refs.add_staff_category_modal.openModal()
    },
    async readStaffSettings() {
      this.loading = true
      try {
        const { data } = (await StaffSettingsRepository.getStaffSettings()).data
        this.staffCategorizationEnabled = data.enabled
        if (this.staffCategorizationEnabled) {
          await this.getStaffCategoryTypesList()
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async updateStaffSettings() {
      this.loading = true
      try {
        const payload = {
          enabled: this.staffCategorizationEnabled,
        }
        const res = await StaffSettingsRepository.updateStaffSettings(payload)
        if (res.status === 200) {
          this.showSuccessMessage('Staff Settings Updated Successfully')
          if (this.staffCategorizationEnabled) {
            await this.getStaffCategoryTypesList()
          } else {
            this.rows = []
          }
        } else {
          this.showErrorMessage('Cannot Update Staff Settings')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
        await this.readStaffSettings()
      }
      this.loading = false
    },
    async getStaffCategoryTypesList() {
      this.dataLoading = true
      this.rows = []
      try {
        const { data } = (await StaffSettingsRepository.getStaffCategoryTypesList(this.page, this.perPage))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getStaffCategoryTypesListNoPagination() {
      try {
        const { data } = (await StaffSettingsRepository.getStaffCategoryTypesListNoPagination())
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async onClickStatusUpdate(id, isActive) {
      try {
        const payload = {
          is_active: isActive,
        }
        const res = await StaffSettingsRepository.updateStaffCategoryTypeStatus(id, payload)
        if (res.status === 200) {
          this.showSuccessMessage(`Staff category type ${isActive ? 'activated' : 'deactivated'} successfully`)
          this.getStaffCategoryTypesList()
        } else {
          this.showErrorMessage(`Couldn't ${isActive ? 'activate' : 'deactivate'} staff category type`)
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    onClickRefresh() {
      this.getStaffCategoryTypesList()
    },
  },
}
</script>

<style scoped lang="scss">
.staff-settings {
  width: calc(100vw - 390px);
}

@media (max-width: 991.98px) {
  .staff-settings {
    width: calc(100vw - 10px);
  }
}
</style>
